/* eslint-disable @typescript-eslint/no-empty-object-type */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-function-type */
/* eslint-disable @typescript-eslint/no-explicit-any */
// 2) Third-party library imports
import { createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

// 3d) Utility functions / interfaces
import { DynamicObject } from '../../utils/commonInterfaces';

// 3e) Redux actions & types
import { DashboardsState } from './types';
import type { RootState } from '../storeTypes';

const actionPrefix = 'dashboards';

// Type-safe initialState!
export const initialState: DashboardsState = {
    loading: false,
    errorMessages: [],
    data: [],
    activeData: {
        record: {},
        loading: false,
        selectedId: null,
        errorMessages: [],
    },
    isEditing: false,
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        getDashboardsRequestAction: (
            state
        ) => {
            state.loading = true;
        },
        getDashboardsSuccessAction: (
            state,
            action: PayloadAction<{
                data: any[];
                notSameCompany: string | boolean;
            }>
        ) => {
            state.loading = false;
            state.data = action.payload.data;
            state.errorMessages = [];
        },
        getDashboardsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = initialState.data;
            state.errorMessages = action.payload;
        },
        clearDashboardsStateDataAction: (state) => {
            Object.assign(state, initialState);
        },
        setDashboardSelectedIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getDashboardDataRequestAction: (
            state,
            _action: PayloadAction<string>
        ) => {
            state.activeData.loading = true;
        },
        getDashboardDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = [];
        },
        getDashboardDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = initialState.activeData.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        setIsEditingDashboard: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.isEditing = action.payload;
        }
    }
});

const { actions, reducer } = dashboardSlice;

// Actions
export const {
    getDashboardsRequestAction,
    getDashboardsSuccessAction,
    getDashboardsErrorAction,
    clearDashboardsStateDataAction,
    setDashboardSelectedIdSuccessAction,
    getDashboardDataRequestAction,
    getDashboardDataSuccessAction,
    getDashboardDataErrorAction,
    setIsEditingDashboard
} = actions;

export const setDashboardSelectedIdRequestAction = createAction(
    `${actionPrefix}/setDashboardSelectedIdRequestAction`,
    function prepare(
        dashboardId: string | null,
        callback?: () => void
    ) {
        return {
            payload: {
                dashboardId,
                callback
            }
        };
    }
);

export const getDashboardInvoicingAndSettlementsRequestAction = createAction(
    `${actionPrefix}/getDashboardInvoicingAndSettlementsRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardNotificationsRequestAction = createAction(
    `${actionPrefix}/getDashboardNotificationsRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardNotificationSuccessRequestAction = createAction(
    `${actionPrefix}/getDashboardNotificationSuccessRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardOpenInvoicesRequestAction = createAction(
    `${actionPrefix}/getDashboardOpenInvoicesRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardCommunicationDeliveryRequestAction = createAction(
    `${actionPrefix}/getDashboardCommunicationDeliveryRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardPaymentsRequestAction = createAction(
    `${actionPrefix}/getDashboardPaymentsRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardPaymentsAllocationRequestAction = createAction(
    `${actionPrefix}/getDashboardPaymentsAllocationRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardCashflowForecastRequestAction = createAction(
    `${actionPrefix}/getDashboardCashflowForecastRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardDailySalesOutRequestAction = createAction(
    `${actionPrefix}/getDashboardDailySalesOutRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTasksCountActionedByUserRequestAction = createAction(
    `${actionPrefix}/getDashboardTasksCountActionedByUserRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTasksCountActionedByUserTypeRequestAction = createAction(
    `${actionPrefix}/getDashboardTasksCountActionedByUserTypeRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTotalOwedRequestAction = createAction(
    `${actionPrefix}/getDashboardTotalOwedRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTicketsForUserTypesRequestAction = createAction(
    `${actionPrefix}/getDashboardTicketsForUserTypesRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTicketsForUsersRequestAction = createAction(
    `${actionPrefix}/getDashboardTicketsForUsersRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTicketsRequestAction = createAction(
    `${actionPrefix}/getDashboardTicketsRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardOpenSupportTicketsRequestAction = createAction(
    `${actionPrefix}/getDashboardOpenSupportTicketsRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardSalesRequestAction = createAction(
    `${actionPrefix}/getDashboardSalesRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTasksCountForUserTypesRequestAction = createAction(
    `${actionPrefix}/getDashboardTasksCountForUserTypesRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTasksCountForUsersRequestAction = createAction(
    `${actionPrefix}/getDashboardTasksCountForUsersRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTasksCountRequestAction = createAction(
    `${actionPrefix}/getDashboardTasksCountRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardCustomersSettlementRequestAction = createAction(
    `${actionPrefix}/getDashboardCustomersSettlementRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardChangeStatisticsByTypeRequestAction = createAction(
    `${actionPrefix}/getDashboardChangeStatisticsByTypeRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardChangeStatisticsRequestAction = createAction(
    `${actionPrefix}/getDashboardChangeStatisticsRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardCurrentATBRequestAction = createAction(
    `${actionPrefix}/getDashboardCurrentATBRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardCustomerCountPBIRequestAction = createAction(
    `${actionPrefix}/getDashboardCustomerCountPBIRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardRecentChangesRequestAction = createAction(
    `${actionPrefix}/getDashboardRecentChangesRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);
export const getDashboardTopCustomersRequestAction = createAction(
    `${actionPrefix}/getDashboardTopCustomersRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTopCreditsRequestAction = createAction(
    `${actionPrefix}/getDashboardTopCreditsRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardTopInvoicesRequestAction = createAction(
    `${actionPrefix}/getDashboardTopInvoicesRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getCashAllocationStatisticsRequestAction = createAction(
    `${actionPrefix}/getCashAllocationStatisticsRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

export const getDashboardCustomerInsightsHistoryRequestAction = createAction(
    `${actionPrefix}/getDashboardCustomerInsightsHistoryRequestAction`,
    function prepare(
        payload: DynamicObject,
        isOrgView?: boolean,
        callback?: Function,
        payloadCallback?: (payload: any) => void
    ) {
        return {
            payload: {
                payload,
                isOrgView,
                callback,
                payloadCallback
            }
        };
    }
);

// Selectors
export const getDashboardsState = (state: RootState) => state.dashboards;
export const getDashboardList = createSelector(
    getDashboardsState,
    (dashboards) => dashboards.data
);
export const getIsEditingDashboard = createSelector(
    getDashboardsState,
    (dashboards) => dashboards.isEditing
);
export const getDashboardActiveData = createSelector(
    getDashboardsState,
    (dashboards) => dashboards.activeData
);
export const getDashboardSelectedId = createSelector(
    getDashboardActiveData,
    (activeData) => activeData.selectedId
);
export const getDashboardActiveErrorMessages = createSelector(
    getDashboardActiveData,
    (activeData) => activeData.errorMessages
);

// Reducer
export default reducer;